import { useId, useState } from 'react'
import { Button } from '@/shared/ui/button'
import { Label } from '@/shared/ui/label'
import { RichTextEditor } from '@/shared/ui/rich-text-editor'
import { t } from '@/shared/lib/i18n'

export const MAX_COMMENTS_LENGTH = 5000

interface ExplanationFormProps {
  warningPrimary: string
  warningSecondary?: string
  commentsPlaceholder: string
  submitLabel: string
  cancelLabel: string
  onSubmit: (comments: string) => void
  onCancel: () => void
  processing: boolean
}

/**
 * Reason-capture form shared by the Ignore (ODE) and Confirm (CNM) review actions.
 * Renders only the body (warning + textarea + footer) so it can be embedded either
 * inside a Dialog (dashboard) or inline within another surface (calendar modal).
 */
export const ExplanationForm = ({
  warningPrimary,
  warningSecondary,
  commentsPlaceholder,
  submitLabel,
  cancelLabel,
  onSubmit,
  onCancel,
  processing,
}: ExplanationFormProps) => {
  const fieldId = useId()
  const [comments, setComments] = useState('')
  const [touched, setTouched] = useState(false)

  const textLength = comments.replace(/<[^>]*>/g, '').length
  const hasError = touched && textLength === 0
  const overLength = textLength > MAX_COMMENTS_LENGTH

  const handleSubmit = () => {
    setTouched(true)
    if (textLength === 0 || overLength) return
    onSubmit(comments)
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1 text-sm">
        <p className="font-medium text-foreground">{warningPrimary}</p>
        {warningSecondary && <p className="text-muted-foreground">{warningSecondary}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor={fieldId}>{t('nonCompliance.dialogs.commentsLabel')}</Label>
        <RichTextEditor
          id={fieldId}
          placeholder={commentsPlaceholder}
          value={comments}
          onChange={setComments}
          className={hasError || overLength ? 'border-destructive' : ''}
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {hasError && (
            <span className="text-destructive">{t('nonCompliance.dialogs.commentsRequired')}</span>
          )}
          {overLength && (
            <span className="text-destructive">
              {t('nonCompliance.dialogs.commentsTooLong').replace(
                '{max}',
                String(MAX_COMMENTS_LENGTH),
              )}
            </span>
          )}
          {!hasError && !overLength && <span />}
          <span className={overLength ? 'text-destructive' : ''}>
            {textLength} / {MAX_COMMENTS_LENGTH}
          </span>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={processing}>
          {cancelLabel}
        </Button>
        <Button onClick={handleSubmit} disabled={processing}>
          {submitLabel}
        </Button>
      </div>
    </div>
  )
}
